<script setup lang="ts">
import {
  HostedFields,
  type hostedFields as hostedFieldsFactory,
  HostedFieldsStateObject,
} from 'braintree-web';
// @ts-expect-error (module exists, necessary to improve tree-shaking)
// eslint-disable-next-line import/no-unresolved
import hostedFields from 'braintree-web/hosted-fields';
import { computed, onMounted, reactive, ref, watch, watchEffect } from 'vue';

import { TokenizedPaymentMethod } from '@/api/paymentMethod';
import FieldErrorIcon from '@/components/base/assets/FieldErrorIcon.vue';
import Checkbox from '@/components/base/form/Checkbox.vue';
import FloatingLabel from '@/components/base/form/FloatingLabel.vue';
import InlineError from '@/components/base/form/InlineError.vue';
import PopperWrapper from '@/components/base/layout/PopperWrapper.vue';
import CreditCardFormSkeleton from '@/components/checkout/steps/payment/CreditCardFormSkeleton.vue';
import { borderRules, sizeRules } from '@/composables/useForm';
import { NutsAddress } from '@/utils/address';

type CardField = 'cvv' | 'expirationDate' | 'number';

const props = defineProps<{
  address: NutsAddress;
  braintreeToken: string;
  canSetAsDefault?: Boolean;
  modelValue?: TokenizedPaymentMethod;
}>();

const emit = defineEmits(['update:modelValue']);

const cardCvvInput = ref<HTMLElement>();
const cardExpirationInput = ref<HTMLElement>();
const cardNumberInput = ref<HTMLElement>();
const creditCardForm = ref<HostedFields>();

const setAsDefault = ref(false);

const tokenizeResult = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const focusHostedField = (fieldName: CardField) => {
  creditCardForm.value?.focus(fieldName);
};

const cardFieldsState = ref<HostedFieldsStateObject['fields']>();

const inputDirtyStatus = reactive<{ [fieldName: string]: boolean }>({
  cvv: false,
  expirationDate: false,
  number: false,
});

const borderColorForField = (fieldName: CardField) => {
  const { [fieldName]: fieldState } = cardFieldsState.value ?? {};
  if (fieldState?.isFocused ?? false) return 'border-black';
  if (inputDirtyStatus[fieldName] && (!fieldState?.isValid ?? false)) return 'border-nuts-red-800';
  return borderRules.color;
};

const compileErrorMessagesForField = (fieldName: CardField) => {
  const descriptors = {
    cvv: 'CVV number',
    expirationDate: 'expiration date',
    number: 'card number',
  };
  const errors = [];
  if (inputDirtyStatus[fieldName]) {
    const { [fieldName]: fieldState } = cardFieldsState.value ?? {};
    if (fieldState?.isEmpty) errors.push(`Please enter the ${descriptors[fieldName]}`);
    if (!fieldState?.isValid) errors.push(`Please enter a valid ${descriptors[fieldName]}`);
  }
  return errors;
};

const errorMessages = computed(() => ({
  cvv: compileErrorMessagesForField('cvv'),
  expirationDate: compileErrorMessagesForField('expirationDate'),
  number: compileErrorMessagesForField('number'),
}));

const fieldHasData = (fieldName: CardField) => {
  const { [fieldName]: fieldState } = cardFieldsState.value ?? {};
  if (!fieldState) return false;
  return !fieldState?.isEmpty ?? false;
};

onMounted(() => {
  const stopWatching = watch(
    () => props.braintreeToken,
    async () => {
      if (!props.braintreeToken) return;
      creditCardForm.value = await (hostedFields as typeof hostedFieldsFactory).create({
        authorization: props.braintreeToken,
        fields: {
          cvv: {
            container: cardCvvInput.value,
          },
          expirationDate: {
            container: cardExpirationInput.value,
          },
          number: {
            container: cardNumberInput.value,
          },
        },
        styles: {
          input: 'font-sans text-base text-black leading-4 p-0',
        },
      });
      cardFieldsState.value = creditCardForm.value.getState()?.fields;

      const trackFieldState = (event: HostedFieldsStateObject) => {
        cardFieldsState.value = event?.fields;
      };

      creditCardForm.value.on('blur', (event) => {
        inputDirtyStatus[event.emittedBy] = true;
        trackFieldState(event);
      });
      creditCardForm.value.on('empty', trackFieldState);
      creditCardForm.value.on('focus', trackFieldState);
      creditCardForm.value.on('notEmpty', trackFieldState);
      creditCardForm.value.on('validityChange', trackFieldState);

      stopWatching();
    },
    { immediate: true },
  );
});

const validCard = computed(() => {
  if (!cardFieldsState.value?.cvv.isValid) return false;
  if (!cardFieldsState.value?.expirationDate.isValid) return false;
  if (!cardFieldsState.value?.number.isValid) return false;
  return true;
});

watchEffect(async () => {
  if (!props.address || !validCard.value) {
    tokenizeResult.value = undefined;
    return;
  }
  try {
    const result = await creditCardForm.value?.tokenize({
      billingAddress: {
        countryCodeAlpha2: props.address.country,
        locality: props.address.city,
        postalCode: props.address.postalCode,
        region: props.address.state,
        streetAddress: props.address.street1,
      },
      cardholderName: props.address.name,
    });
    tokenizeResult.value = result
      ? {
          ...result,
          billingAddress: { ...props.address },
          setAsDefault: setAsDefault.value,
        }
      : undefined;
  } catch (e) {
    console.error('Failed to tokenize card info:', e);
    tokenizeResult.value = undefined;
  }
});

const { 'img/cvv-examples.svg': cvvExamplesSrc, 'img/info-icon-filled.svg': infoIconFilled } =
  nutshell;

const borderUtilities = [borderRules.width, borderRules.style, 'rounded-lg'];
const sizeUtilities = [sizeRules.height, sizeRules.padding, sizeRules.width];
</script>

<template>
  <div>
    <transition name="fade" :duration="2000">
      <div v-show="creditCardForm">
        <div class="relative w-full">
          <div
            class="bg-white"
            :class="[borderUtilities, sizeUtilities, borderColorForField('number')]"
            data-test="card-number-input-container"
            ref="cardNumberInput"
            @click="focusHostedField('number')"
          />
          <FloatingLabel
            :active="fieldHasData('number')"
            :invalid="errorMessages.number.length > 0"
            alternateBackground
          >
            Card Number*
          </FloatingLabel>
          <FieldErrorIcon :showErrorIcon="errorMessages.number.length > 0" />
          <InlineError :errors="errorMessages.number" fieldName="Credit Card Number" />
        </div>

        <div class="flex items-center mt-6">
          <div class="relative w-7/12 md:w-5/12">
            <div
              class="bg-white"
              :class="[borderUtilities, sizeUtilities, borderColorForField('expirationDate')]"
              data-test="card-expiration-input-container"
              ref="cardExpirationInput"
              @click="focusHostedField('expirationDate')"
            />
            <FloatingLabel
              :active="fieldHasData('expirationDate')"
              :invalid="errorMessages.expirationDate.length > 0"
              alternateBackground
            >
              Expiry MM/YY*
            </FloatingLabel>
            <FieldErrorIcon :showErrorIcon="errorMessages.expirationDate.length > 0" />
            <InlineError :errors="errorMessages.expirationDate" fieldName="Credit Card Exp. Date" />
          </div>
          <div class="relative w-4/12 ml-4">
            <div
              class="bg-white"
              :class="[borderUtilities, sizeUtilities, borderColorForField('cvv')]"
              data-test="card-cvv-input-container"
              ref="cardCvvInput"
              @click="focusHostedField('cvv')"
            />
            <FloatingLabel
              :active="fieldHasData('cvv')"
              :invalid="errorMessages.cvv.length > 0"
              alternateBackground
            >
              CVV*
            </FloatingLabel>
            <FieldErrorIcon :showErrorIcon="errorMessages.cvv.length > 0" />
            <InlineError :errors="errorMessages.cvv" fieldName="Credit Card CVV" />
          </div>
          <div class="tiny-note popover-container">
            <PopperWrapper
              arrow
              class="legacy-max-width"
              offsetDistance="10"
              placement="bottom-start"
            >
              <template #content>
                <div class="p-3 text-xs text-neutral-500" data-test="cvv-popper">
                  <img :src="cvvExamplesSrc" alt="cvv examples" class="w-32" />
                </div>
              </template>
              <img
                role="button"
                aria-describedby="cvv-popper-locator"
                alt="CVV info"
                class="ml-2 transform -translate-y-0.5 bg-black cursor-pointer h-3.5 md:h-4 rounded-full text-tiny text-white w-3.5 md:w-4"
                :src="infoIconFilled"
              />
            </PopperWrapper>
            <span id="cvv-popper-locator" class="sr-only"> CVV info is shown in a tooltip </span>
          </div>
        </div>
      </div>
    </transition>
    <CreditCardFormSkeleton v-show="!creditCardForm" />
    <div v-if="canSetAsDefault" class="mt-4">
      <Checkbox
        v-model="setAsDefault"
        class="text-sm font-normal leading-4"
        data-test="save-card-checkbox"
      >
        Save and set as default for all future payments
      </Checkbox>
    </div>
  </div>
</template>
